import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import * as api from '../services/api'
import { VersionConflictError } from '../services/api'

const useUserStore = create(
  persist(
    (set, get) => ({
      user: {
        email: '',
        fullName: '',
        rowVersion: null,
        emailNotifications: true,
        emailOnAssignment: true,
        emailOnWatchedUpdate: true,
        emailOnDueDateReminder: true,
        reminderDefault: '1_day'
      },
      currentProjectId: null,
      authToken: null,

      // Version conflict state (set when API returns 409 conflict)
      versionConflict: null,

      // Loading state for API operations
      loading: false,
      error: null,

      updateUser: (updates) =>
        set((state) => ({
          user: {
            ...state.user,
            ...updates
          }
        })),

      // Fetch user profile from API (includes rowVersion)
      fetchUser: async () => {
        set({ loading: true, error: null })
        try {
          const user = await api.getCurrentUser()
          set((state) => ({
            user: {
              ...state.user,
              ...user
            },
            loading: false
          }))
          return user
        } catch (error) {
          set({ error: error.message, loading: false })
          throw error
        }
      },

      // Update user profile via API with rowVersion
      updateProfile: async (updates) => {
        set({ loading: true, error: null })
        try {
          const currentUser = get().user
          const updatedUser = await api.updateUserProfile(updates, currentUser.rowVersion)
          set((state) => ({
            user: {
              ...state.user,
              ...updatedUser
            },
            loading: false
          }))
          return updatedUser
        } catch (error) {
          if (error instanceof VersionConflictError) {
            // Auto-refresh user data
            await get().fetchUser()
            set({
              versionConflict: { type: 'user', message: error.message },
              loading: false
            })
            return null
          }
          set({ error: error.message, loading: false })
          throw error
        }
      },

      setCurrentProject: (id) =>
        set({ currentProjectId: id ? Number(id) : null }),

      setAuthToken: (token) =>
        set({ authToken: token }),

      isAuthenticated: () => !!get().authToken,

      setVersionConflict: (conflict) =>
        set({ versionConflict: conflict }),

      clearVersionConflict: () =>
        set({ versionConflict: null }),

      clearError: () => set({ error: null }),

      resetUser: () =>
        set({
          user: {
            email: '',
            fullName: '',
            rowVersion: null,
            emailNotifications: true,
            emailOnAssignment: true,
            emailOnWatchedUpdate: true,
            emailOnDueDateReminder: true,
            reminderDefault: '1_day'
          },
          currentProjectId: null,
          authToken: null,
          versionConflict: null
        })
    }),
    {
      name: 'user-storage'
    }
  )
)

export default useUserStore
